import React from 'react'
import AvatarComponent from 'components/avatar'
import dayjs from 'dayjs'
import { DefaultProfileIcon } from 'components/icons'

export type MsgCardComponentProps = {
  text: string
  files?: string[]
  sender: string
  sentAt: string
  photoLink?: string
  failedPhotoLinks: Set<string>
  onFailedPhotoLink: (url: string) => void
  isMyMsg?: boolean
  isPrivateChat?: boolean
  isEdited?: boolean
}

const MsgCardComponent: React.FC<MsgCardComponentProps> = ({
  text,
  sender,
  sentAt,
  photoLink,
  failedPhotoLinks,
  onFailedPhotoLink,
  isMyMsg = false,
  isPrivateChat = false,
  isEdited = false,
  files,
}) => {
  const formattedTime = dayjs(sentAt).format('MMM D, h:mm A') // Example format: "Oct 15, 2:45 PM"

  // Convert new line characters to <br /> elements
  const formattedText = text.split('\n').map((line, index) => (
    <React.Fragment key={index}>
      {line}
      <br />
    </React.Fragment>
  ))

  return (
    <div className={`flex ${isMyMsg ? 'flex-row-reverse' : 'flex-row'} mb-2 px-4`} style={{ gap: '8px' }}>
      {/* Avatar - Only show for others in group chats */}
      {!isMyMsg && !isPrivateChat && (
        <div className='flex-shrink-0'>
          <AvatarComponent
            size={8}
            name={sender}
            photoLink={photoLink}
            failedPhotoLinks={failedPhotoLinks}
            onFailedPhotoLink={onFailedPhotoLink}
            defaultElement={<DefaultProfileIcon width={8} height={8} />}
          />
        </div>
      )}
      <div className={`flex flex-col ${isMyMsg ? 'items-end' : 'items-start'}`}>
        {/* Message Bubble */}
        <div
          className={`rounded-2xl transition-all duration-200 ease-in-out ${
            isMyMsg
              ? 'bg-custom-yellow hover:bg-yellow-400 rounded-br-sm'
              : 'bg-white hover:bg-gray-50 rounded-bl-sm shadow-sm'
          } ${isMyMsg ? 'text-gray-900' : 'text-gray-900'}`}
          style={{
            maxWidth: 'min(calc(100vw - 120px), 500px)',
            padding: '12px 16px',
            wordBreak: 'break-word',
            boxShadow: isMyMsg ? '0 1px 2px rgba(0, 0, 0, 0.1)' : '0 1px 3px rgba(0, 0, 0, 0.08)',
          }}
        >
          <div className='flex flex-col w-full'>
            {!isMyMsg && !isPrivateChat && (
              <span className='text-[13px] font-semibold text-gray-800 mb-0.5'>{sender}</span>
            )}
            <div className='flex flex-col'>
              <p className='text-[15px] leading-[1.5] break-words'>{formattedText}</p>
              <div className='flex items-center justify-end mt-1 space-x-1'>
                {isEdited && <span className='text-xs text-gray-600'>· Edited</span>}
                <span className='text-xs text-gray-600'>{formattedTime}</span>
              </div>
            </div>
          </div>
          {files && files.length > 0 && (
            <div className='mt-2 flex flex-wrap gap-2'>
              {files.map((file, index) => (
                <a
                  key={index}
                  href={file}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-sm text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-150 flex items-center gap-1.5 bg-blue-50 px-2.5 py-1.5 rounded-lg border border-blue-100'
                >
                  <svg
                    className='w-3.5 h-3.5'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                    />
                  </svg>
                  File {index + 1}
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MsgCardComponent
